import { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { authAPI } from '../api/auth';

/**
 * Custom hook for authentication
 * Provides access to auth state and methods
 */
export const useAuth = () => {
  const {
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    isAuthenticated,
    setUser,
    setTokens,
    setLoading,
    setError,
    logout,
    clearError,
  } = useAuthStore();

  const [autoRefreshInProgress, setAutoRefreshInProgress] = useState(false);

  /**
   * Login user with email and password
   */
  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      setTokens(response.access_token, response.refresh_token);
      setUser(response.user);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register customer
   */
  const registerCustomer = async (data: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.registerCustomer(data);
      setTokens(response.access_token, response.refresh_token);
      setUser(response.user);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Registration failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register store owner
   */
  const registerStore = async (data: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.registerStore(data);
      setTokens(response.access_token, response.refresh_token);
      setUser(response.user);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Registration failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register delivery agent
   */
  const registerDelivery = async (data: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.registerDelivery(data);
      setTokens(response.access_token, response.refresh_token);
      setUser(response.user);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Registration failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh access token
   */
  const refresh = async () => {
    if (!refreshToken || autoRefreshInProgress) {
      return;
    }

    setAutoRefreshInProgress(true);
    try {
      const response = await authAPI.refreshToken(refreshToken);
      setTokens(response.access_token, response.refresh_token);
      // If we have a user, update it too
      if (response.user) {
        setUser(response.user);
      }
    } catch (err: any) {
      logout();
    } finally {
      setAutoRefreshInProgress(false);
    }
  };

  /**
   * Get current user info
   */
  const getCurrentUser = async () => {
    setLoading(true);
    try {
      const user = await authAPI.getCurrentUser();
      setUser(user);
      return user;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to get user';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   */
  const handleLogout = () => {
    logout();
  };

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    isAuthenticated,

    // Methods
    login,
    registerCustomer,
    registerStore,
    registerDelivery,
    refresh,
    getCurrentUser,
    logout: handleLogout,
    clearError,
  };
};
